import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import moment from 'moment/min/moment-with-locales';
import { Moment } from 'moment';
import { DialogActions } from 'src/app/model/dictionary';
import { PACO_DATE_PROVIDERS } from 'src/app/model/paco-date-providers';
import { PacoDialogAction } from 'src/app/components/dialogs/paco-dialog/paco-dialog.component';
import { DataExchangeRequestApiService } from 'src/app/data-transfer/services/data-exchange-request-api-service';
import { DataExchangeCommentDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-comment-dto';
import { DataExchangeSubRequestDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-sub-request-dto';
import { DataExchangeSubRequestPaginationFields } from 'src/app/data-transfer/entities/pagination-fields';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';

export interface ReopenDecisionDialogData {
  requestId: number;
  requestName: string | null;
  deadline: string | null;
}

export interface ReopenDecisionDialogResult {
  event: DialogActions;
}

export type ReopenDecision = 'accept' | 'reject';

@Component({
  selector: 'app-reopen-decision-dialog',
  templateUrl: './reopen-decision-dialog.component.html',
  styleUrls: ['./reopen-decision-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: PACO_DATE_PROVIDERS,
})
export class ReopenDecisionDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<ReopenDecisionDialogComponent>);
  private readonly data = inject<ReopenDecisionDialogData>(MAT_DIALOG_DATA);
  private readonly dataExchangeRequestApiService = inject(DataExchangeRequestApiService);

  readonly displayedColumns = ['id', 'name', 'articleNumber', 'requestedAt', 'comment', 'decision'];
  readonly pageSizeOptions = [5, 10, 25];
  readonly dataSource = new PaginationDataSource<DataExchangeSubRequestDto>(query => this.dataExchangeRequestApiService.getSubRequestsByPage(query, true));

  readonly decisions = signal<ReadonlyMap<number, ReopenDecision>>(new Map());
  readonly allIds = signal<number[]>([]);
  readonly totalCount = signal(0);
  readonly pageIndex = signal(0);
  readonly pageSize = signal(this.pageSizeOptions[1]);
  readonly isSaving = signal(false);
  readonly errorMessageKey = signal<string | null>(null);

  readonly acceptedIds = computed(() => this.idsWithDecision('accept'));
  readonly rejectedIds = computed(() => this.idsWithDecision('reject'));
  readonly undecidedCount = computed(() => this.totalCount() - this.decisions().size);

  readonly deadlineControl = new FormControl<Moment | null>({ value: this.data.deadline ? moment(this.data.deadline) : null, disabled: true });
  private readonly deadlineValue = toSignal(this.deadlineControl.valueChanges, { initialValue: this.deadlineControl.value });
  readonly isDeadlineInPast = computed(() => {
    const value = this.deadlineValue();
    return !!value && value.isBefore(moment().startOf('day'));
  });

  readonly dialogActions = computed<PacoDialogAction[]>(() => [
    { name: DialogActions.REJECT, labelKey: 'common.buttons.cancel', disabled: this.isSaving() },
    { name: DialogActions.CONFIRM, labelKey: 'common.buttons.confirm', color: 'primary', disabled: this.decisions().size === 0 || this.isSaving() },
  ]);

  constructor() {
    this.dataSource.page.pipe(takeUntilDestroyed()).subscribe(page => {
      this.allIds.set(page.allIds);
      this.totalCount.set(page.totalItemCount);
      this.pageIndex.set(page.pageIndex);
      this.decisions.update(decisions => new Map([...decisions].filter(([id]) => page.allIds.includes(id))));
    });

    effect(() => {
      if (this.acceptedIds().length > 0) {
        this.deadlineControl.enable({ emitEvent: false });
      } else {
        this.deadlineControl.disable({ emitEvent: false });
      }
    });

    this.dataSource.fetchData(
      0,
      this.pageSize(),
      [
        { fieldName: DataExchangeSubRequestPaginationFields.DataExchangeRequestId, values: [this.data.requestId.toString()] },
        { fieldName: DataExchangeSubRequestPaginationFields.HasPendingReopen, values: ['true'] },
      ],
      DataExchangeSubRequestPaginationFields.Id,
      true
    );
  }

  getName(sub: DataExchangeSubRequestDto): string {
    return sub.packagingComponent?.articleName ?? sub.multiMaterial?.articleName ?? sub.packagingUnitInfo?.productName ?? sub.packagingSystemInfo?.productName ?? '';
  }

  getArticleNumber(sub: DataExchangeSubRequestDto): string {
    return sub.packagingComponent?.articleNumber ?? sub.multiMaterial?.articleNumber ?? sub.packagingUnitInfo?.articleNumber ?? sub.packagingSystemInfo?.articleNumber ?? '';
  }

  getDate(dateStr: string | null): Date | null {
    return dateStr ? new Date(dateStr) : null;
  }

  getLatestComment(sub: DataExchangeSubRequestDto): DataExchangeCommentDto | null {
    const comments = sub.comments ?? [];
    return comments.reduce<DataExchangeCommentDto | null>(
      (latest, comment) => (!latest || comment.creationTimestamp > latest.creationTimestamp ? comment : latest),
      null
    );
  }

  getDecision(id: number): ReopenDecision | undefined {
    return this.decisions().get(id);
  }

  decide(id: number, decision: ReopenDecision): void {
    this.decisions.update(decisions => {
      const next = new Map(decisions);
      if (next.get(id) === decision) {
        next.delete(id);
      } else {
        next.set(id, decision);
      }
      return next;
    });
  }

  decideAll(decision: ReopenDecision): void {
    this.decisions.set(new Map(this.allIds().map(id => [id, decision])));
  }

  clearDeadline(): void {
    this.deadlineControl.setValue(null);
  }

  onPageChanged(event: PageEvent): void {
    this.pageSize.set(event.pageSize);
    this.dataSource.fetchData(event.pageIndex, event.pageSize);
  }

  onDialogAction(action: DialogActions): void {
    if (action === DialogActions.CONFIRM) {
      this.confirm();
    } else {
      this.cancel();
    }
  }

  confirm(): void {
    if (this.decisions().size === 0 || this.isSaving()) {
      return;
    }
    const value = this.deadlineControl.value;
    const deadline = value ? value.format('YYYY-MM-DD') : null;
    this.errorMessageKey.set(null);
    this.isSaving.set(true);
    this.dataExchangeRequestApiService
      .decideReopen(this.data.requestId, { acceptedSubRequestIds: this.acceptedIds(), rejectedSubRequestIds: this.rejectedIds(), deadline }, true)
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.dialogRef.close({ event: DialogActions.CONFIRM } satisfies ReopenDecisionDialogResult);
        },
        error: (error: HttpErrorResponse) => {
          this.isSaving.set(false);
          if (error.status === 409) {
            this.errorMessageKey.set('dataManagement.dataExchangeRequests.reopenDecisionDialog.conflictError');
            this.dataSource.refresh();
          } else {
            this.errorMessageKey.set('dataManagement.dataExchangeRequests.reopenDecisionDialog.genericError');
          }
        },
      });
  }

  cancel(): void {
    this.dialogRef.close({ event: DialogActions.REJECT } satisfies ReopenDecisionDialogResult);
  }

  private idsWithDecision(decision: ReopenDecision): number[] {
    return [...this.decisions()].filter(([, value]) => value === decision).map(([id]) => id);
  }
}
